import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FileCheck, 
  Shield, 
  Clock, 
  AlertTriangle,
  Upload,
  Inbox,
  CheckCircle2,
  ArrowRight,
  Eye,
  QrCode,
  Share2
} from 'lucide-react';
import { PortalLayout } from './PortalLayout';
import { useExchangeAuth, exchangeApi } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';
import { useCredentialAnimation } from '../CredentialAnimations';

const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
  <div className="glass-card p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-slate-400">{title}</p>
        <p className={`text-3xl font-heading font-bold text-${color} mt-1`}>{value}</p>
        {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl bg-${color}/10 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}`} />
      </div>
    </div>
  </div>
);

export const DriverDashboard = () => {
  const { user } = useExchangeAuth();
  const [credentials, setCredentials] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Credential animations hook
  const animations = useCredentialAnimation();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [credsRes, reqsRes] = await Promise.all([
        exchangeApi.getCredentials(),
        exchangeApi.getIncomingRequests()
      ]);
      setCredentials(credsRes.data.credentials || []);
      setRequests(reqsRes.data.requests || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle credential upload with animation
  const handleCredentialUpload = async (credentialData) => {
    // Show encryption animation
    await animations.encrypt({
      title: "Securing Your Credential",
      subtitle: "End-to-end encryption with AES-256"
    });
    
    // Show vault lock animation
    await animations.lock({
      title: "Storing in Secure Vault"
    });
    
    toast.success("Credential uploaded and secured!");
    loadData();
  };
  
  // Handle credential share with animation
  const handleShareCredential = async (credentialId, recipientId) => {
    // Show verification animation
    await animations.verify({
      title: "Verifying Permissions"
    });
    
    // Show transfer animation
    await animations.transfer({
      title: "Sharing Credential",
      fromLabel: "Your Vault",
      toLabel: "Recipient"
    });
    
    toast.success("Credential shared securely!");
  };
  
  // Handle request approval with animation
  const handleApproveRequest = async (requestId) => {
    // Show fingerprint scan animation
    await animations.scan({
      title: "Biometric Authorization"
    });
    
    // Show approval animation
    await animations.approve({
      title: "Request Approved"
    });
    
    toast.success("Request approved!");
    loadData();
  };
  
  // Handle request rejection with animation
  const handleRejectRequest = async (requestId) => {
    await animations.reject({
      title: "Request Declined"
    });
    
    toast.info("Request declined");
    loadData();
  };

  // Calculate stats
  const verifiedCount = credentials.filter(c => c.status === 'verified').length;
  const expiringCount = credentials.filter(c => c.status === 'expiring_soon').length;
  const pendingRequests = requests.filter(r => r.status === 'pending').length;

  return (
    <PortalLayout role="driver">
      {/* Credential Animations */}
      <animations.AnimationComponent />
      
      <div className="space-y-6">
        {/* Welcome */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">
              Welcome back, {user?.first_name}!
            </h1>
            <p className="text-slate-400">Manage your credentials and access requests</p>
          </div>
          <Link to="/portal/driver/credentials" className="btn-primary">
            <Upload className="w-4 h-4 mr-2" />
            Upload Credential
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Verified Credentials"
            value={verifiedCount}
            icon={CheckCircle2}
            color="neon-green"
            subtitle={`${credentials.length} total`}
          />
          <StatCard
            title="Expiring Soon"
            value={expiringCount}
            icon={Clock}
            color={expiringCount > 0 ? 'yellow' : 'slate'}
            subtitle="Next 30 days"
          />
          <StatCard
            title="Pending Requests"
            value={pendingRequests}
            icon={Inbox}
            color={pendingRequests > 0 ? 'neon-cyan' : 'slate'}
            subtitle="Awaiting your approval"
          />
          <StatCard
            title="Shared With"
            value={credentials.reduce((acc, c) => acc + (c.shared_with?.length || 0), 0)}
            icon={Share2}
            color="neon-purple"
            subtitle="Organizations"
          />
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Credential Passport */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6 border-neon-cyan/30"
          >
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-16 h-16 rounded-xl bg-neon-cyan/20 flex items-center justify-center">
                <QrCode className="w-8 h-8 text-neon-cyan" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">Your Credential Passport</h3>
                <p className="text-sm text-slate-400">Share your verified credentials instantly</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
              <div className="flex items-center space-x-3">
                <CheckCircle2 className={`w-5 h-5 ${verifiedCount > 0 ? 'text-neon-green' : 'text-slate-500'}`} />
                <span className="text-sm text-slate-300">
                  {verifiedCount > 0 ? `${verifiedCount} credentials verified` : 'No verified credentials yet'}
                </span>
              </div>
              <button className="text-neon-cyan text-sm hover:underline flex items-center">
                View QR <ArrowRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </motion.div>

          {/* Pending Requests */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4">Access Requests</h3>
            {pendingRequests > 0 ? (
              <div className="space-y-3">
                {requests.filter(r => r.status === 'pending').slice(0, 3).map((req) => (
                  <div key={req.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                    <div>
                      <p className="text-sm text-white">{req.requester_name}</p>
                      <p className="text-xs text-slate-500">
                        {req.credential_types?.length || 0} credential(s) requested
                      </p>
                    </div>
                    <Link
                      to={`/portal/driver/requests`}
                      className="text-xs text-neon-cyan hover:underline"
                    >
                      Review
                    </Link>
                  </div>
                ))}
                {pendingRequests > 3 && (
                  <Link
                    to="/portal/driver/requests"
                    className="text-sm text-slate-400 hover:text-white flex items-center"
                  >
                    View all {pendingRequests} requests <ArrowRight className="w-4 h-4 ml-1" />
                  </Link>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <Inbox className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400">No pending requests</p>
              </div>
            )}
          </motion.div>
        </div>

        {/* Recent Credentials */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">My Credentials</h3>
            <Link to="/portal/driver/credentials" className="text-sm text-neon-cyan hover:underline">
              View all
            </Link>
          </div>
          
          {credentials.length > 0 ? (
            <div className="space-y-3">
              {credentials.slice(0, 5).map((cred) => (
                <div key={cred.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      cred.status === 'verified' ? 'bg-neon-green/20' :
                      cred.status === 'expiring_soon' ? 'bg-yellow-500/20' :
                      cred.status === 'expired' ? 'bg-red-500/20' :
                      'bg-white/10'
                    }`}>
                      <FileCheck className={`w-5 h-5 ${
                        cred.status === 'verified' ? 'text-neon-green' :
                        cred.status === 'expiring_soon' ? 'text-yellow-400' :
                        cred.status === 'expired' ? 'text-red-400' :
                        'text-slate-400'
                      }`} />
                    </div>
                    <div>
                      <p className="text-white font-medium">{cred.name}</p>
                      <p className="text-xs text-slate-500 capitalize">
                        {cred.credential_type?.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      cred.status === 'verified' ? 'bg-neon-green/20 text-neon-green' :
                      cred.status === 'expiring_soon' ? 'bg-yellow-500/20 text-yellow-400' :
                      cred.status === 'expired' ? 'bg-red-500/20 text-red-400' :
                      cred.status === 'pending' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-white/10 text-slate-400'
                    }`}>
                      {cred.status?.replace(/_/g, ' ')}
                    </span>
                    <button className="p-2 text-slate-400 hover:text-white">
                      <Eye className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileCheck className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400 mb-4">No credentials uploaded yet</p>
              <Link to="/portal/driver/credentials" className="btn-primary">
                Upload Your First Credential
              </Link>
            </div>
          )}
        </motion.div>

        {/* Expiring Alert */}
        {expiringCount > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6 border-yellow-500/30 bg-yellow-500/5"
          >
            <div className="flex items-start space-x-4">
              <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
              </div>
              <div className="flex-1">
                <h4 className="text-white font-medium mb-1">Credentials Expiring Soon</h4>
                <p className="text-sm text-slate-400 mb-3">
                  {expiringCount} credential{expiringCount > 1 ? 's' : ''} will expire in the next 30 days. 
                  Update them to maintain your verified status.
                </p>
                <Link to="/portal/driver/credentials" className="text-yellow-400 text-sm hover:underline">
                  View expiring credentials →
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </PortalLayout>
  );
};

export default DriverDashboard;
